import { PlantsGetResponse } from '@app/api/plants';
import { TFunction } from 'i18next';
import { Roles } from '../../userManagementUtils';
import { isValidGuid } from '@app/utils/guidHelpers';
import { DomainType } from '@app/api/users/ProfileFilterResponse';

/**
 * Type alias for plant reference objects with id and name
 */
export type PlantReference = Array<{ id: string; name: string }>;

/**
 * Type alias for userPlants parameter (can be single ID, array of IDs/names, or undefined)
 */
export type UserPlantsInput = string | string[] | undefined;

/**
 * RHI Magnesita domains for frontend domain type detection
 */
const RHI_MAGNESITA_DOMAINS = new Set(['rhimagnesita.com', 'contractor.rhimagnesita.com']);

/**
 * Normalizes an email domain by removing @ and converting to lowercase
 * @param email - The email address to normalize
 * @returns The normalized domain or empty string if invalid
 */
export const normalizeDomain = (email: string): string => {
  if (!email?.includes('@')) return '';
  const domain = email.split('@')[1];
  return domain?.toLowerCase().trim() ?? '';
};

/**
 * Detects if an email belongs to RHI Magnesita domain
 * @param email - The email address to check
 * @returns true if it is a RHI Magnesita domain, false otherwise
 */
export const isRhiMagnesitaDomain = (email: string): boolean => {
  if (!email?.includes('@')) return false;

  const parts = email.split('@');
  // Ensure there's content before the @ (valid email format)
  if (!parts[0] || parts[0].trim().length === 0) return false;

  const domain = normalizeDomain(email);
  return RHI_MAGNESITA_DOMAINS.has(domain);
};

/**
 * Validates that multiple emails belong to compatible domains
 * RHI Magnesita domains can be invited together, customer domains from the same plant can be invited together,
 * but mixing RHI MAGNESITA with customer domains or customer domains from different plants is not allowed
 *
 * @param emails - Array of email addresses to validate
 * @param t - Translation function for error messages
 * @returns Object with isValid flag and errorMessage if invalid
 */
export const validateMultipleDomains = (
  emails: string[],
  t: TFunction
): { isValid: boolean; errorMessage: string } => {
  if (emails.length <= 1) {
    return { isValid: true, errorMessage: '' };
  }

  const validEmails = emails.filter(email => email?.includes('@'));
  if (validEmails.length <= 1) {
    return { isValid: true, errorMessage: '' };
  }

  const domainTypes = new Set<string>();

  validEmails.forEach(email => {
    const domain = normalizeDomain(email);
    if (!domain) return;

    const isRhiMagnesita = RHI_MAGNESITA_DOMAINS.has(domain);

    if (isRhiMagnesita) {
      domainTypes.add('RHI Magnesita');
    } else {
      domainTypes.add(domain);
    }
  });

  if (domainTypes.size > 1) {
    return {
      isValid: false,
      errorMessage: t('domain.validation.multiple-domains.error',
        'Cannot invite users from different domains simultaneously. Manage invitations separately by domain.')
    };
  }

  return { isValid: true, errorMessage: '' };
};

/**
 * Gets the plant IDs from the user plants selection
 * Handles different input types: array of plant names, single plant index, or plant admin default
 *
 * @param userPlants - The user plants selection (array or string)
 * @param allPlants - Array of all available plants
 * @param isPlantAdmin - Whether the current user is a plant admin
 * @param selectedPlantId - The currently selected plant ID (for plant admin)
 * @returns Array of plant IDs
 */
export const getUserPlantsValue = (
  userPlants: string[] | string,
  allPlants: PlantsGetResponse[],
  isPlantAdmin: boolean,
  selectedPlantId?: string
): string[] => {

  // Flow for Plant Admins (A plant admin is a customer user) inviting other users from his/her plant
  if (isPlantAdmin) {
    const result = [selectedPlantId || ''];
    return result;
  }

  // Flow for internal users invited by System Admins
  // Internal users can be assigned to multiple plants
  if (Array.isArray(userPlants)) {
    const result = userPlants.map(plantName =>
      allPlants.find(plant => plant.name === plantName)?.id ?? ''
    );
    return result;
  }

  // Flow for customer users invited by System Admins
  if (userPlants) {
    return [userPlants];
  }

  // Default case (should not happen): return empty array
  return [];
};

/**
 * Extracts a valid plant ID from userPlants field (which can be string, array, or undefined)
 *
 * This function handles the different formats that userPlants can have:
 * - Array: Takes the first element if exists (can be plant ID or plant name)
 * - String: Uses the string directly (should be plant ID)
 * - Undefined: Returns undefined
 *
 * **Important:** When userPlants contains plant names (from FormAutocompleteWithTags),
 * pass the plants array to convert names to IDs.
 *
 * Additionally, validates that the extracted plantId is a valid GUID (not empty GUID)
 *
 * @param userPlants - The userPlants value from the form (string | string[] | undefined)
 * @param plants - Optional array of plants to convert plant names to IDs
 * @returns Valid plant GUID or undefined
 *
 * @example
 * ```ts
 * // With plant IDs
 * extractPlantIdFromUserPlants(['abc-123', 'def-456']) // 'abc-123'
 * extractPlantIdFromUserPlants('xyz-789') // 'xyz-789'
 *
 * // With plant names (requires plants array)
 * const plants = [{ id: 'abc-123', name: 'Plant A' }]
 * extractPlantIdFromUserPlants(['Plant A'], plants) // 'abc-123'
 *
 * extractPlantIdFromUserPlants([]) // undefined
 * extractPlantIdFromUserPlants(undefined) // undefined
 * extractPlantIdFromUserPlants(['00000000-0000-0000-0000-000000000000']) // undefined (empty GUID)
 * ```
 */
export const extractPlantIdFromUserPlants = (
  userPlants: UserPlantsInput,
  plants?: PlantReference
): string | undefined => {
  if (!userPlants) return undefined;

  let plantId: string | undefined;

  // Regex to validate GUID format: 8-4-4-4-12 hexadecimal characters
  const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  if (Array.isArray(userPlants)) {
    const firstValue = userPlants.length > 0 ? userPlants[0] : undefined;

    // If it's a valid GUID format, check if not empty GUID
    if (firstValue && guidRegex.test(firstValue) && isValidGuid(firstValue)) {
      plantId = firstValue;
    } else if (plants && firstValue) {
      // If it's a plant name, convert to ID
      const plant = plants.find(p => p.name === firstValue);
      plantId = plant?.id;
    }
  } else {
    plantId = userPlants;
  }

  // Return undefined if plantId is empty GUID or undefined
  const result = isValidGuid(plantId) ? plantId : undefined;
  return result;
};

/**
 * Validates if all strings in an array are valid email addresses
 * @param emails - Array of strings to validate
 * @returns true if all strings are valid emails, false otherwise
 */
export const validateEmails = (emails: string[]): boolean => {
  if (!Array.isArray(emails) || emails.length === 0) {
    return false;
  }

  // Maximum email length according to RFC 5321 is 254 characters
  const MAX_EMAIL_LENGTH = 254;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return emails.every(item => {
    // Prevent ReDoS: reject excessively long inputs before regex test
    if (!item || item.length > MAX_EMAIL_LENGTH || item.length < 3) {
      return false;
    }
    return emailRegex.test(item);
  });
};

/**
 * Determines if a role allows selection of multiple plants
 * Multi-plant roles: Admin, KeyUser, TechnicalConsultant
 * @param roleId - The role ID as string or number
 * @returns true if role allows multiple plants, false otherwise
 */
export const isMultiPlantRole = (roleId: string | number): boolean => {
  const roleValue = typeof roleId === 'string' ? Number.parseInt(roleId, 10) : roleId;
  return roleValue === Roles.Admin ||
         roleValue === Roles.KeyUser ||
         roleValue === Roles.TechnicalConsultant;
};

/**
 * Calculates the userPlants value when role changes
 * Preserves current selection when staying in the same role type (multi-plant → multi-plant or single-plant → single-plant)
 * Only resets when changing between types or when no current value exists
 * @param roleId - The selected role ID
 * @param isPlantAdmin - Whether current user is a plant admin
 * @param inviterSelectedPlant - The active selected plant of the inviter (that can be Plant Admin or a System Admin)
 * @param currentUserPlants - The current userPlants value to preserve if staying in same role type
 * @returns The userPlants value for the new users based on the new role and inviter's permissions
 */
export const calculateUserPlantsOnRoleChange = (
  roleId: string,
  isPlantAdmin: boolean,
  inviterSelectedPlant?: string,
  currentUserPlants?: string | string[]
): string[] | string | undefined => {
  // Plant admin always uses their selected plant
  if (isPlantAdmin) {
    return [inviterSelectedPlant || ''];
  }

  const newRoleIsMultiPlant = isMultiPlantRole(roleId);
  const currentIsArray = Array.isArray(currentUserPlants);
  const currentIsString = typeof currentUserPlants === 'string' && currentUserPlants !== '';

  // If staying in multi-plant role type and already has array value, preserve it
  if (newRoleIsMultiPlant && currentIsArray) {
    return currentUserPlants; // Preserve current selection
  }

  // If staying in single-plant role type and already has string value, preserve it
  if (!newRoleIsMultiPlant && currentIsString) {
    return currentUserPlants; // Preserve current selection
  }

  // Changing role type or no current value: return appropriate empty value
  if (newRoleIsMultiPlant) {
    return []; // Multi-plant role needs empty array
  }

  return undefined; // Single-plant role needs undefined (will be set when plant selected)
};

/**
 * Toggles a plant in the selection array
 * @param currentSelection - Current array of selected plant names
 * @param plantToToggle - Plant name to add or remove
 * @param checked - Whether to add (true) or remove (false)
 * @returns New array with plant toggled
 */
export const togglePlantSelection = (
  currentSelection: string[],
  plantToToggle: string,
  checked: boolean
): string[] => {
  if (!Array.isArray(currentSelection)) {
    return checked ? [plantToToggle] : [];
  }

  if (checked) {
    // Add plant if not already present
    return currentSelection.includes(plantToToggle)
      ? currentSelection
      : [...currentSelection, plantToToggle];
  } else {
    // Remove plant
    return currentSelection.filter(item => item !== plantToToggle);
  }
};

/**
 * Handles the logic for logins autocomplete onChange event
 * Determines the appropriate action based on the change reason and email validation
 * @param value - The new value from autocomplete (can be string, array, or null)
 * @param reason - The reason for the change (e.g., 'clear', 'select-option')
 * @returns Object with action type and new value to apply
 */
export const handleLoginsChange = (
  value: string | string[] | null,
  reason: string
): { action: 'clear' | 'update' | 'ignore'; newValue: string[] | null } => {
  if (reason === 'clear') {
    return { action: 'clear', newValue: [] };
  } else if (validateEmails(value as string[])) {
    return { action: 'update', newValue: value as string[] };
  }
  return { action: 'ignore', newValue: null };
};

/**
 * Action result for profile filtering operations
 */
export type ProfileFilterAction =
  | { action: 'clear' }
  | { action: 'filter'; email: string; plantId: string }
  | { action: 'skip' };

/**
 * Determines what action to take for profile filtering based on validation state
 *
 * Optimization: RHI Magnesita users can invite to any RHI role (Admin, KeyUser, TechnicalConsultant)
 * without backend validation. This skips unnecessary API calls for RHI Magnesita domains.
 *
 * @param multiDomainIsValid - Whether multiple domain validation passed
 * @param logins - Array of email logins
 * @param selectedPlantId - The ID of the currently selected plant
 * @returns Object with action type and optional filter parameters
 */
export const determineProfileFilterAction = (
  multiDomainIsValid: boolean,
  logins: string[],
  selectedPlantId: string | undefined
): ProfileFilterAction => {

  // If multiple domains are invalid, clear filters
  if (!multiDomainIsValid) {
    return { action: 'clear' };
  }

  // Check if we have the required data to filter
  if (logins.length > 0 && selectedPlantId) {
    const firstEmail = logins[0];

    // Only filter if the email contains @
    if (firstEmail?.includes('@')) {
      // Optimization: Skip API call for RHI Magnesita domains
      // RHI Magnesita users can invite to any RHI role (Admin, KeyUser, TechnicalConsultant)
      if (isRhiMagnesitaDomain(firstEmail)) {
        return { action: 'skip' };
      }

      // For customer domains, call API to validate permissions
      return {
        action: 'filter',
        email: firstEmail,
        plantId: selectedPlantId
      };
    }
  }

  // Otherwise, clear filters
  return { action: 'clear' };
};

/**
 * Configuration options for filtering roles
 */
export interface RoleFilterConfig<T extends { value: number }> {
  multipleDomainError: string;
  logins: string[];
  isPlantAdmin: boolean;
  allowedProfiles: string[]; // Backend returns role names (e.g., 'PlantAdmin', 'Supervisor')
  hasError: boolean;
  allRoles: T[];
  restrictedRoles: number[];
}

/**
 * Filters available roles based on domain validation state and user permissions
 * @param config - Configuration object containing all filtering parameters
 * @returns Filtered array of roles available for selection
 */
export const filterRolesByDomainAndUser = <T extends { value: number }>(
  config: RoleFilterConfig<T>
): T[] => {
  const {
    multipleDomainError,
    logins,
    isPlantAdmin,
    allowedProfiles,
    hasError,
    allRoles,
    restrictedRoles
  } = config;

  // If there are multiple domain errors, disable roles
  if (multipleDomainError) {
    return [];
  }

  // If we have a valid email entered
  if (logins.length > 0 && logins[0]?.includes('@')) {
    const email = logins[0];

    // Detect RHI Magnesita domain on frontend (independent of API call)
    const isRhiMagnesitaDomainValue = isRhiMagnesitaDomain(email);

    // Additional security check: If current user is plant admin and trying to invite RHI Magnesita domain, block it
    if (isPlantAdmin && isRhiMagnesitaDomainValue) {
      return []; // Plant admin cannot invite RHI Magnesita users - block completely
    }

    // For RHI Magnesita domains: filter roles on frontend (no API call needed)
    if (isRhiMagnesitaDomainValue) {
      const rhiMagnesitaRoles = new Set([Roles.Admin, Roles.KeyUser, Roles.TechnicalConsultant]);
      return allRoles.filter(role => rhiMagnesitaRoles.has(role.value));
    }

    // For customer domains: show customer roles immediately (API validation happens later)
    // The API profiles/allowed is used for AUTHORIZATION (checking if domain is registered in plant)
    // NOT for determining which roles to show (that's determined by domain type)
    const customerRoles = new Set([Roles.PlantAdmin, Roles.Supervisor, Roles.Operator]);

    // If API already responded with specific allowed profiles, use those
    if (allowedProfiles.length > 0) {
      // Backend returns role names (roleKeys), so we need to match by roleKey
      const allowedRoleKeys = new Set(allowedProfiles);
      return allRoles.filter(role => {
        const roleKey = (role as any).roleKey;
        return roleKey && allowedRoleKeys.has(roleKey);
      });
    } else if (hasError) {
      // API error - still show customer roles but validation will prevent submission
      return allRoles.filter(role => customerRoles.has(role.value));
    } else {
      // No API response yet (waiting for plant selection) - show customer roles
      return allRoles.filter(role => customerRoles.has(role.value));
    }
  } else {
    // No email entered yet - show default roles based on plant admin status
    return allRoles.filter(r =>
      (isPlantAdmin && !restrictedRoles.includes(r.value)) ||
      (!isPlantAdmin)
    );
  }
};

/**
 * Determines if the currently selected role should be reset
 * because it's no longer in the list of allowed profiles
 * @param currentRoleId - The currently selected role ID (as string)
 * @param allowedProfiles - Array of allowed profile names (e.g., 'PlantAdmin', 'Supervisor')
 * @param allRoles - Array of all available roles with roleKey mapping
 * @returns true if the role should be reset, false otherwise
 */
export const shouldResetRoleSelection = (
  currentRoleId: string | undefined,
  allowedProfiles: string[],
  allRoles: { value: number; roleKey?: string }[]
): boolean => {
  // Don't reset if no role is selected
  if (!currentRoleId) {
    return false;
  }

  // Don't reset if there are no allowed profiles (not yet validated)
  if (allowedProfiles.length === 0) {
    return false;
  }

  // Parse the current role ID to number
  const currentRoleValue = Number.parseInt(currentRoleId, 10);

  // Find the roleKey for the current role
  const currentRole = allRoles.find(role => role.value === currentRoleValue);
  const currentRoleKey = (currentRole as any)?.roleKey;

  // Reset if the current role's key is not in the allowed profiles
  return !currentRoleKey || !allowedProfiles.includes(currentRoleKey);
};

/**
 * Determines the appropriate domain validation message based on domain type and user permissions
 * @param isPlantAdmin - Whether the current user is a plant admin
 * @param domainType - The type of domain detected (RHI, Customer, Unknown)
 * @param hasError - Whether there was an error validating the domain
 * @param t - Translation function
 * @returns The appropriate validation message or empty string
 */
export const getDomainValidationMessage = (
  isPlantAdmin: boolean,
  domainType: DomainType | undefined,
  hasError: boolean,
  t: (key: string, defaultValue?: string) => string
): string => {
  // Plant admin trying to invite RHI Magnesita domain - block with specific message
  if (isPlantAdmin && domainType === DomainType.RhiMagnesita) {
    return t(
      'domain.validation.customer-user-cannot-invite-rhimagnesita.message',
      'Customer users cannot invite RHI Magnesita users. Please contact a system administrator.'
    );
  }

  // Unknown domain
  if (domainType === DomainType.Unknown) {
    return t(
      'domain.validation.unknown.blocked.message',
      'Unauthorized domain for this plant. Configure the domain in plant settings or use an RHI Magnesita domain for invitations.'
    );
  }

  // API error
  if (hasError) {
    return t('domain.validation.error.message', 'Error validating domain. Please try again.');
  }

  return '';
};

/**
 * Normalizes the userPlants value to match the expected type for the role
 * - Multi-plant roles (Admin, KeyUser, TechnicalConsultant) expect an array
 * - Single-plant roles expect a string
 *
 * This is a safety net to ensure type consistency. The actual preservation logic
 * happens in calculateUserPlantsOnRoleChange which runs BEFORE this function.
 *
 * @param roleId - The role ID to check
 * @param currentValue - Current value of userPlants (can be string or array)
 * @returns Object with shouldUpdate flag and newValue if type conversion is needed
 *
 * @example
 * // Role is Admin (multi-plant), but userPlants is a string (type mismatch)
 * normalizeUserPlantsValue('1', 'plant-id-123')
 * // Returns: { shouldUpdate: true, newValue: [] } - Converts to array
 *
 * @example
 * // Role is Operator (single-plant), but userPlants is an array (type mismatch)
 * normalizeUserPlantsValue('2', ['plant-1', 'plant-2'])
 * // Returns: { shouldUpdate: true, newValue: '' } - Converts to string
 *
 * @example
 * // Role is Admin and userPlants is already an array - no update needed
 * normalizeUserPlantsValue('1', [])
 * // Returns: { shouldUpdate: false, newValue: undefined }
 */
export const normalizeUserPlantsValue = (
  roleId: string,
  currentValue: string | string[] | undefined
): { shouldUpdate: boolean; newValue?: string | string[] } => {
  if (!roleId) {
    return { shouldUpdate: false };
  }

  const shouldBeArray = isMultiPlantRole(roleId);

  // If should be array but is string, convert to array
  if (shouldBeArray && typeof currentValue === 'string') {
    return { shouldUpdate: true, newValue: [] };
  }

  // If should be string but is array, convert to string
  if (!shouldBeArray && Array.isArray(currentValue)) {
    return { shouldUpdate: true, newValue: '' };
  }

  // Type already matches - no update needed
  return { shouldUpdate: false };
};
